import { ModuleManifest } from "../model";

/** The source used to load a module manifest at runtime. */
export type ModuleSource = "federation";

/** Federation coordinates needed to fetch a remote manifest module. */
export interface FederationRemoteDescriptor {
  readonly remoteEntry: string;
  readonly scope: string;
  readonly exposedModule?: string;
}

/** Runtime descriptor for a module listed in a shell manifest. */
export interface ModuleDescriptor {
  readonly id: string;
  readonly metadataPath?: string;
  readonly federation?: FederationRemoteDescriptor;
}

/** Browser event payload for module load lifecycle telemetry. */
export interface ModuleLoadEventDetail {
  readonly moduleId: string;
  readonly context: "core" | "app";
  readonly source: ModuleSource;
  readonly phase: "start" | "success" | "failure";
  readonly message?: string;
}

/** Browser event payload for detailed shell module diagnostics. */
export interface ModuleDiagnosticEventDetail {
  readonly moduleId: string;
  readonly context: "core" | "app";
  readonly stage:
  | "descriptor-validate"
  | "remote-bootstrap"
  | "container-get"
  | "manifest-resolve"
  | "load-manifest";
  readonly status: "start" | "success" | "failure" | "retry";
  readonly source: ModuleSource;
  readonly attempt?: number;
  readonly message?: string;
  readonly buildEpoch?: string;
  readonly timestamp: string;
}

/** Browser event payload when a module descriptor ID and its metadata ID differ. */
export interface ModuleIdMismatchEventDetail {
  readonly context: "core" | "app";
  readonly moduleId: string;
  readonly metadataId: string;
  readonly metadataPath: string;
}

/** Minimal Webpack Federation container API used by the shell. */
interface WebpackContainer {
  init(shareScope: unknown): Promise<void>;
  get(request: string): Promise<() => unknown>;
}

/** Resolved manifest and the source used to load it. */
type LoadedModuleResult = {
  readonly source: ModuleSource;
  readonly module: ModuleManifest;
};

declare const __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: Record<string, unknown>;

const remoteContainerRegistry: Map<string, Promise<WebpackContainer>> = new Map();
const REMOTE_SCRIPT_LOAD_TIMEOUT_MS = 8000;
const FEDERATION_MAX_ATTEMPTS = 3;
const FEDERATION_RETRY_BASE_DELAY_MS = 300;

function getRemoteContainerKey(scope: string, remoteEntry: string): string {
  return `${scope}|${remoteEntry}`;
}

/** Returns true only when running in a browser-like runtime. */
function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function getBuildEpoch(): string | undefined {
  const hostGlobal = globalThis as typeof globalThis & {
    __CAPRX_BUILD_EPOCH__?: string;
  };

  return hostGlobal.__CAPRX_BUILD_EPOCH__;
}

/** Emits lifecycle events for module loading (start, success, failure). */
function emitModuleLoadEvent(detail: ModuleLoadEventDetail): void {
  if (!isBrowser()) return;

  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-load", {
      detail,
    }),
  );
}

/** Emits detailed shell diagnostics for module loading internals. */
function emitModuleDiagnosticEvent(detail: Omit<ModuleDiagnosticEventDetail, "timestamp" | "buildEpoch">): void {
  if (!isBrowser()) return;

  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-diagnostic", {
      detail: {
        ...detail,
        buildEpoch: getBuildEpoch(),
        timestamp: new Date().toISOString(),
      } as ModuleDiagnosticEventDetail,
    }),
  );
}

function validateFederationDescriptor(moduleId: string, federation: FederationRemoteDescriptor): void {
  const remoteEntry = federation.remoteEntry?.trim();
  const scope = federation.scope?.trim();

  if (!remoteEntry) {
    throw new Error(`Module [${moduleId}] federation.remoteEntry must be a non-empty string.`);
  }

  if (!scope) {
    throw new Error(`Module [${moduleId}] federation.scope must be a non-empty string.`);
  }
}

async function sleep(delayMs: number): Promise<void> {
  await new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function getRetryDelayMs(attempt: number): number {
  const exponent = Math.max(attempt - 1, 0);
  return FEDERATION_RETRY_BASE_DELAY_MS * 2 ** exponent;
}

/** The remote container's own window property, cast once rather than at each call site. */
function getHostWindow(): Record<string, unknown> {
  return window as unknown as Record<string, unknown>;
}

function remoteEntryScriptSelector(remoteEntry: string): string {
  const escape = globalThis.CSS?.escape;
  const value = escape ? escape(remoteEntry) : remoteEntry.replace(/["\\]/g, "\\$&");
  return `script[data-caprx-remote-entry="${value}"]`;
}

/*
  A missing scope or a bad manifest export fails identically on every attempt, so
  only script fetch and timeout failures are worth retrying.
*/
function isRetryableFederationError(error: unknown): boolean {
  if (!(error instanceof Error)) return true;

  const deterministic = ["is not available after loading", "does not contain", "is not a valid module namespace"];
  return !deterministic.some((fragment) => error.message.includes(fragment));
}

/** Extracts and validates the default manifest export from a remote module. */
function getManifestFromRemoteModule(candidate: unknown): ModuleManifest {
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error("Loaded module is not a valid module namespace object.");
  }

  const manifestContainer = candidate as { default?: ModuleManifest };
  const manifest = manifestContainer.default;
  if (!manifest || typeof manifest !== "object") {
    throw new Error("Loaded module does not contain a default manifest export.");
  }

  return manifest;
}

/** Loads the remote entry URL once, reusing the script tag on repeat calls. */
function ensureRemoteScriptLoaded(remoteEntry: string, scope: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const cleanup = (script: HTMLScriptElement, onLoad: () => void, onError: () => void) => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    const attachHandlers = (script: HTMLScriptElement) => {
      const onLoad = () => {
        script.dataset.caprxLoadState = "loaded";
        script.dataset.loaded = "true";
        cleanup(script, onLoad, onError);
        resolve();
      };

      const onError = () => {
        script.dataset.caprxLoadState = "failed";
        cleanup(script, onLoad, onError);
        script.remove();
        reject(new Error(`Unable to load federation remote script [${remoteEntry}].`));
      };

      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", onError, { once: true });

      timeout = setTimeout(() => {
        script.dataset.caprxLoadState = "failed";
        cleanup(script, onLoad, onError);
        script.remove();
        reject(new Error(`Timed out while loading federation remote script [${remoteEntry}] after ${REMOTE_SCRIPT_LOAD_TIMEOUT_MS}ms.`));
      }, REMOTE_SCRIPT_LOAD_TIMEOUT_MS);
    };

    const existingScript = document.querySelector(remoteEntryScriptSelector(remoteEntry)) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.dataset.caprxScope = scope;

      if (existingScript.dataset.loaded === "true" || existingScript.dataset.caprxLoadState === "loaded") {
        resolve();
        return;
      }

      if (existingScript.dataset.caprxLoadState === "failed") {
        existingScript.remove();
      } else {
        attachHandlers(existingScript);
        return;
      }
    }

    const script = document.createElement("script");
    script.src = remoteEntry;
    script.async = true;
    script.type = "text/javascript";
    script.dataset.caprxRemoteEntry = remoteEntry;
    script.dataset.caprxScope = scope;
    script.dataset.caprxLoadState = "loading";
    script.crossOrigin = "anonymous";

    attachHandlers(script);

    document.head.appendChild(script);
  });
}

/** Gets or creates an initialized federation container for scope + remote entry. */
async function getOrCreateFederationContainer(remoteEntry: string, scope: string): Promise<WebpackContainer> {
  const key = getRemoteContainerKey(scope, remoteEntry);
  const existing = remoteContainerRegistry.get(key);
  if (existing) return existing;

  const promise = (async () => {
    await ensureRemoteScriptLoaded(remoteEntry, scope);

    const hostWindow = getHostWindow();

    const container = hostWindow[scope] as WebpackContainer | undefined;
    if (!container || typeof container.get !== "function" || typeof container.init !== "function") {
      throw new Error(`Remote scope [${scope}] is not available after loading [${remoteEntry}].`);
    }

    await __webpack_init_sharing__("default");
    await container.init(__webpack_share_scopes__.default);

    return container;
  })();

  remoteContainerRegistry.set(key, promise);

  try {
    return await promise;
  } catch (err) {
    remoteContainerRegistry.delete(key);
    throw err;
  }
}

/** Clears federation runtime caches for the provided modules (or all modules if omitted). */
export function clearFederationRuntimeCache(modules?: ReadonlyArray<ModuleDescriptor> | null): void {
  if (!isBrowser()) {
    remoteContainerRegistry.clear();
    return;
  }

  if (!modules) {
    const hostWindow = getHostWindow();
    const scopesToDelete = new Set<string>();

    for (const key of remoteContainerRegistry.keys()) {
      const [scope] = key.split("|");
      if (scope) {
        scopesToDelete.add(scope);
      }
    }

    document.querySelectorAll("script[data-caprx-remote-entry]").forEach((scriptNode) => {
      const script = scriptNode as HTMLScriptElement;
      const scriptScope = script.dataset.caprxScope;
      if (scriptScope) {
        scopesToDelete.add(scriptScope);
      }
    });

    for (const scope of scopesToDelete) {
      if (Object.prototype.hasOwnProperty.call(hostWindow, scope)) {
        delete hostWindow[scope];
      }
    }

    remoteContainerRegistry.clear();

    document.querySelectorAll("script[data-caprx-remote-entry]").forEach((script) => script.remove());

    return;
  }

  const hostWindow = getHostWindow();

  for (const descriptor of modules) {
    const federation = descriptor.federation;
    if (!federation) {
      continue;
    }

    const key = getRemoteContainerKey(federation.scope, federation.remoteEntry);
    remoteContainerRegistry.delete(key);

    const script = document.querySelector(remoteEntryScriptSelector(federation.remoteEntry)) as HTMLScriptElement | null;
    if (script) {
      script.remove();
    }

    if (Object.prototype.hasOwnProperty.call(hostWindow, federation.scope)) {
      delete hostWindow[federation.scope];
    }
  }
}

/** Loads a manifest from a module federation remote endpoint. */
async function loadManifestFromFederation(
  context: "core" | "app",
  moduleId: string,
  config: FederationRemoteDescriptor,
): Promise<ModuleManifest> {
  if (!isBrowser()) {
    throw new Error("Federation loading is only available in browser environments.");
  }

  const exposedModule = config.exposedModule || "./manifest";

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "remote-bootstrap",
    status: "start",
    source: "federation",
  });

  const container = await getOrCreateFederationContainer(config.remoteEntry, config.scope);

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "remote-bootstrap",
    status: "success",
    source: "federation",
  });

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "container-get",
    status: "start",
    source: "federation",
  });

  const factory = await container.get(exposedModule);

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "container-get",
    status: "success",
    source: "federation",
  });

  const loaded = factory();
  return getManifestFromRemoteModule(loaded);
}

async function loadManifestWithRetry(
  context: "core" | "app",
  moduleId: string,
  descriptor: ModuleDescriptor,
  federation: FederationRemoteDescriptor,
): Promise<ModuleManifest> {
  let previousError: unknown;

  for (let attempt = 1; attempt <= FEDERATION_MAX_ATTEMPTS; attempt++) {
    emitModuleDiagnosticEvent({
      moduleId,
      context,
      stage: "load-manifest",
      status: "start",
      source: "federation",
      attempt,
    });

    try {
      const manifest = await loadManifestFromFederation(context, moduleId, federation);

      emitModuleDiagnosticEvent({
        moduleId,
        context,
        stage: "manifest-resolve",
        status: "success",
        source: "federation",
        attempt,
      });

      return manifest;
    } catch (error) {
      previousError = error;
      const message = error instanceof Error ? error.message : String(error);

      emitModuleDiagnosticEvent({
        moduleId,
        context,
        stage: "load-manifest",
        status: "failure",
        source: "federation",
        attempt,
        message,
      });

      clearFederationRuntimeCache([descriptor]);

      if (attempt < FEDERATION_MAX_ATTEMPTS && isRetryableFederationError(error)) {
        const delayMs = getRetryDelayMs(attempt);

        emitModuleDiagnosticEvent({
          moduleId,
          context,
          stage: "load-manifest",
          status: "retry",
          source: "federation",
          attempt,
          message: `Retrying manifest load in ${delayMs}ms.`,
        });

        await sleep(delayMs);
        continue;
      }

      throw previousError;
    }
  }

  if (previousError instanceof Error) {
    throw previousError;
  }

  throw new Error(`Module [${moduleId}] could not be loaded from federation.`);
}

export async function loadModuleManifest(context: "core" | "app", descriptor: ModuleDescriptor): Promise<LoadedModuleResult> {
  const { id: moduleId, federation } = descriptor;

  if (!federation) {
    throw new Error(`Module [${moduleId}] is missing federation configuration.`);
  }

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "descriptor-validate",
    status: "start",
    source: "federation",
  });

  validateFederationDescriptor(moduleId, federation);

  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "descriptor-validate",
    status: "success",
    source: "federation",
  });

  emitModuleLoadEvent({ moduleId, context, source: "federation", phase: "start" });

  try {
    const module = await loadManifestWithRetry(context, moduleId, descriptor, federation);
    emitModuleLoadEvent({ moduleId, context, source: "federation", phase: "success" });
    return { source: "federation", module };
  } catch (err) {
    emitModuleLoadEvent({
      moduleId,
      context,
      source: "federation",
      phase: "failure",
      message: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}
